import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Calendar, MapPin, Clock, Users, RefreshCw, Loader2, Route,
  Zap, AlertTriangle, CheckCircle2, TrendingUp, Car, UserCheck,
  CalendarClock, ChevronRight, Timer, Target, Navigation
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const getStatusColor = (status) => {
  const colors = {
    'optimal': 'bg-emerald-100 text-emerald-700',
    'overloaded': 'bg-red-100 text-red-700',
    'underutilized': 'bg-amber-100 text-amber-700',
    'available': 'bg-emerald-100 text-emerald-700',
    'pto': 'bg-blue-100 text-blue-700',
    'weekend': 'bg-gray-100 text-gray-500'
  };
  return colors[status] || 'bg-gray-100 text-gray-700';
};

const getPriorityColor = (priority) => {
  const colors = {
    'urgent': 'bg-red-100 text-red-700',
    'priority': 'bg-amber-100 text-amber-700',
    'routine': 'bg-blue-100 text-blue-700'
  };
  return colors[priority] || 'bg-gray-100 text-gray-700';
};

export default function IntelligentScheduling() {
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [overview, setOverview] = useState(null);
  const [routes, setRoutes] = useState(null);
  const [availability, setAvailability] = useState(null);
  const [skillMatching, setSkillMatching] = useState(null);
  const [ptoImpact, setPtoImpact] = useState(null);
  const [autoSchedule, setAutoSchedule] = useState(null);

  const fetchData = async (retryCount = 0) => {
    setLoading(true);
    const token = localStorage.getItem('qapi-token');
    
    if (!token && retryCount < 3) {
      setTimeout(() => fetchData(retryCount + 1), 1000);
      return;
    }
    
    const headers = { 'Authorization': `Bearer ${token}` };

    try {
      const [overviewRes, routesRes, availRes, skillRes] = await Promise.all([
        fetch(`${API_URL}/api/scheduling/overview`, { headers }),
        fetch(`${API_URL}/api/scheduling/routes`, { headers }),
        fetch(`${API_URL}/api/scheduling/availability`, { headers }),
        fetch(`${API_URL}/api/scheduling/skill-matching`, { headers })
      ]);

      if (overviewRes.status === 401 && retryCount < 2) {
        setTimeout(() => fetchData(retryCount + 1), 1000);
        return;
      }

      if (overviewRes.ok) setOverview(await overviewRes.json());
      if (routesRes.ok) setRoutes(await routesRes.json());
      if (availRes.ok) setAvailability(await availRes.json());
      if (skillRes.ok) setSkillMatching(await skillRes.json());
    } catch (error) {
      console.error('Error fetching scheduling data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchPTOImpact = async () => {
    const token = localStorage.getItem('qapi-token');
    try {
      const res = await fetch(`${API_URL}/api/scheduling/pto-impact?days=1`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) setPtoImpact(await res.json());
    } catch (error) {
      console.error('Error fetching PTO impact:', error);
    }
  };

  const generateAutoSchedule = async () => {
    const token = localStorage.getItem('qapi-token');
    try {
      const res = await fetch(`${API_URL}/api/scheduling/auto-schedule`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) setAutoSchedule(await res.json());
    } catch (error) {
      console.error('Error generating auto-schedule:', error);
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => fetchData(), 100);
    return () => clearTimeout(timer);
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
        <span className="ml-2">Loading scheduling data...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6" data-testid="intelligent-scheduling-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Calendar className="h-7 w-7 text-blue-500" />
            Intelligent Scheduling
          </h1>
          <p className="text-muted-foreground">AI-powered visit scheduling and route optimization</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={generateAutoSchedule} variant="default" size="sm">
            <Zap className="h-4 w-4 mr-2" />
            Auto-Schedule
          </Button>
          <Button onClick={fetchData} variant="outline" size="sm">
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card data-testid="visits-today-card">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Visits Today</p>
                <p className="text-3xl font-bold">{overview?.summary?.visits_today || 0}</p>
              </div>
              <CalendarClock className="h-8 w-8 text-blue-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Optimization Score</p>
                <p className="text-3xl font-bold text-emerald-600">{overview?.summary?.optimization_score || 0}%</p>
              </div>
              <Target className="h-8 w-8 text-emerald-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Time Saved</p>
                <p className="text-2xl font-bold text-violet-600">{overview?.summary?.potential_travel_time_saved || '0 min'}</p>
              </div>
              <Timer className="h-8 w-8 text-violet-500" />
            </div>
            <p className="text-xs text-muted-foreground mt-1">per day with optimization</p>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Active Nurses</p>
                <p className="text-3xl font-bold">{overview?.summary?.total_nurses || 0}</p>
              </div>
              <Users className="h-8 w-8 text-amber-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="routes">Route Optimization</TabsTrigger>
          <TabsTrigger value="availability">Availability</TabsTrigger>
          <TabsTrigger value="skills">Skill Matching</TabsTrigger>
          <TabsTrigger value="pto">PTO Impact</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="mt-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Workload Balance */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Users className="h-5 w-5" />
                  Workload Balance
                </CardTitle>
                <CardDescription>
                  {overview?.workload_balance?.balanced ? 
                    'Workload is well-balanced' : 
                    'Some nurses are overloaded or underutilized'}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {overview?.workload_balance?.details?.slice(0, 10).map((nurse, idx) => (
                    <div key={idx} className="flex items-center justify-between p-2 rounded-lg bg-muted/30">
                      <div className="flex items-center gap-3">
                        <UserCheck className="h-4 w-4 text-muted-foreground" />
                        <span className="font-medium text-sm">{nurse.nurse_name}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-sm">{nurse.visits_today} visits</span>
                        <Badge className={getStatusColor(nurse.status)}>
                          {nurse.status}
                        </Badge>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="mt-4 grid grid-cols-3 gap-4 text-center">
                  <div className="p-2 bg-emerald-50 rounded-lg">
                    <p className="text-xs text-muted-foreground">Optimal</p>
                    <p className="text-lg font-bold text-emerald-600">
                      {overview?.workload_balance?.details?.filter(d => d.status === 'optimal').length || 0}
                    </p>
                  </div>
                  <div className="p-2 bg-red-50 rounded-lg">
                    <p className="text-xs text-muted-foreground">Overloaded</p>
                    <p className="text-lg font-bold text-red-600">
                      {overview?.workload_balance?.overloaded_count || 0}
                    </p>
                  </div>
                  <div className="p-2 bg-amber-50 rounded-lg">
                    <p className="text-xs text-muted-foreground">Underutilized</p>
                    <p className="text-lg font-bold text-amber-600">
                      {overview?.workload_balance?.underutilized_count || 0}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Recommendations */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Zap className="h-5 w-5 text-yellow-500" />
                  Scheduling Recommendations
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {overview?.recommendations?.map((rec, idx) => (
                    <div key={idx} className={`p-4 rounded-lg border-l-4 ${
                      rec.priority === 'high' ? 'border-l-red-500 bg-red-50' :
                      rec.priority === 'medium' ? 'border-l-amber-500 bg-amber-50' :
                      'border-l-blue-500 bg-blue-50'
                    }`}>
                      <div className="flex items-start gap-2">
                        <ChevronRight className="h-5 w-5 mt-0.5 shrink-0" />
                        <div>
                          <p className="font-medium text-sm">{rec.message}</p>
                          <p className="text-xs text-muted-foreground mt-1">{rec.action}</p>
                          <Badge variant="outline" className="mt-2 text-xs">
                            Impact: {rec.impact}
                          </Badge>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Auto-Schedule Results */}
            {autoSchedule && (
              <Card className="lg:col-span-2">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <CheckCircle2 className="h-5 w-5 text-emerald-500" />
                    Auto-Schedule Results - {autoSchedule.date}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-4 gap-4 mb-4">
                    <div className="p-3 bg-blue-50 rounded-lg text-center">
                      <p className="text-sm text-muted-foreground">Total Visits</p>
                      <p className="text-2xl font-bold">{autoSchedule.summary?.total_visits}</p>
                    </div>
                    <div className="p-3 bg-emerald-50 rounded-lg text-center">
                      <p className="text-sm text-muted-foreground">Scheduled</p>
                      <p className="text-2xl font-bold text-emerald-600">{autoSchedule.summary?.scheduled_count}</p>
                    </div>
                    <div className="p-3 bg-amber-50 rounded-lg text-center">
                      <p className="text-sm text-muted-foreground">Unscheduled</p>
                      <p className="text-2xl font-bold text-amber-600">{autoSchedule.summary?.unscheduled_count}</p>
                    </div>
                    <div className="p-3 bg-violet-50 rounded-lg text-center">
                      <p className="text-sm text-muted-foreground">Success Rate</p>
                      <p className="text-2xl font-bold text-violet-600">{autoSchedule.summary?.scheduling_success_rate}%</p>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {Object.entries(autoSchedule.optimization_applied || {}).map(([key, value]) => (
                      <Badge key={key} variant="outline" className={value ? 'bg-emerald-50' : ''}>
                        {value ? <CheckCircle2 className="h-3 w-3 mr-1" /> : null}
                        {key.replace(/_/g, ' ')}
                      </Badge>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </TabsContent>

        <TabsContent value="routes" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Navigation className="h-5 w-5 text-blue-500" />
                Optimized Routes
              </CardTitle>
              <CardDescription>
                Route optimization saves {routes?.summary?.total_time_saved_minutes || 0} minutes total 
                ({routes?.summary?.avg_time_saved_per_nurse || 0} min/nurse)
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {routes?.routes?.slice(0, 8).map((route, idx) => (
                  <div key={idx} className="border rounded-lg p-4">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <Car className="h-5 w-5 text-muted-foreground" />
                        <span className="font-semibold">{route.nurse_name}</span>
                      </div>
                      <div className="flex items-center gap-4">
                        <Badge variant="outline">{route.total_visits} visits</Badge>
                        <Badge className="bg-emerald-100 text-emerald-700">
                          -{route.time_saved_minutes} min
                        </Badge>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-2 mb-3">
                      <Progress value={route.route_efficiency} className="h-2 flex-1" />
                      <span className="text-sm font-medium">{route.route_efficiency}% efficient</span>
                    </div>
                    
                    <div className="flex flex-wrap gap-2">
                      {route.visits?.slice(0, 6).map((visit, vIdx) => (
                        <div key={vIdx} className="flex items-center gap-1 text-xs bg-muted/50 px-2 py-1 rounded">
                          <span className="font-medium">{visit.sequence}.</span>
                          <span>{visit.visit_type}</span>
                          <Badge className={`${getPriorityColor(visit.priority)} text-[10px] px-1`}>
                            {visit.priority}
                          </Badge>
                        </div>
                      ))}
                      {route.visits?.length > 6 && (
                        <span className="text-xs text-muted-foreground">+{route.visits.length - 6} more</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="availability" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle>Nurse Availability - Next 7 Days</CardTitle>
              <CardDescription>
                {availability?.summary?.nurses_on_pto || 0} nurses have PTO scheduled
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left p-2">Nurse</th>
                      {availability?.nurses?.[0]?.daily_availability?.map((day, idx) => (
                        <th key={idx} className="text-center p-2 min-w-[80px]">
                          <div>{day.day}</div>
                          <div className="text-xs text-muted-foreground">{day.date?.slice(5)}</div>
                        </th>
                      ))}
                      <th className="text-center p-2">Total Hrs</th>
                    </tr>
                  </thead>
                  <tbody>
                    {availability?.nurses?.slice(0, 12).map((nurse, idx) => (
                      <tr key={idx} className="border-b hover:bg-muted/30">
                        <td className="p-2 font-medium">{nurse.nurse_name}</td>
                        {nurse.daily_availability?.map((day, dIdx) => (
                          <td key={dIdx} className="text-center p-2">
                            <Badge className={`${getStatusColor(day.status)} text-xs`}>
                              {day.status === 'available' ? day.scheduled_visits : day.status.charAt(0).toUpperCase()}
                            </Badge>
                          </td>
                        ))}
                        <td className="text-center p-2 font-semibold">{nurse.total_available_hours}h</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="skills" className="mt-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle>Nurse Skills & Specialties</CardTitle>
                <CardDescription>
                  {skillMatching?.summary?.expert_nurses || 0} expert nurses, 
                  {skillMatching?.summary?.high_acuity_certified || 0} high-acuity certified
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3 max-h-96 overflow-y-auto">
                  {skillMatching?.nurse_skills?.map((nurse, idx) => (
                    <div key={idx} className="p-3 border rounded-lg">
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-medium">{nurse.nurse_name}</span>
                        <Badge className={
                          nurse.skill_level === 'expert' ? 'bg-violet-100 text-violet-700' :
                          nurse.skill_level === 'experienced' ? 'bg-blue-100 text-blue-700' :
                          'bg-gray-100 text-gray-700'
                        }>
                          {nurse.skill_level}
                        </Badge>
                      </div>
                      <div className="flex flex-wrap gap-1">
                        {nurse.specialties?.map((spec, sIdx) => (
                          <Badge key={sIdx} variant="outline" className="text-xs">
                            {spec.replace('_', ' ')}
                          </Badge>
                        ))}
                        {nurse.high_acuity_certified && (
                          <Badge className="bg-red-100 text-red-700 text-xs">High Acuity</Badge>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Patient-Nurse Matching</CardTitle>
                <CardDescription>AI-recommended skill-based assignments</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {skillMatching?.matching_recommendations?.map((match, idx) => (
                    <div key={idx} className="p-3 border rounded-lg">
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-medium">{match.patient}</span>
                        <Badge variant="outline">{match.specialty_needed.replace('_', ' ')}</Badge>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-muted-foreground">Best Match:</span>
                        <Badge className={
                          match.match_quality === 'excellent' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'
                        }>
                          {match.best_match}
                        </Badge>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="pto" className="mt-4">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>PTO Impact Analysis</CardTitle>
                  <CardDescription>Forecast coverage impact when nurses take time off</CardDescription>
                </div>
                <Button onClick={fetchPTOImpact} variant="outline" size="sm">
                  Analyze Impact
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {ptoImpact ? (
                <div className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="p-4 border rounded-lg">
                      <p className="text-sm text-muted-foreground">Nurse on PTO</p>
                      <p className="text-xl font-bold">{ptoImpact.pto_details?.nurse_name}</p>
                      <p className="text-sm">{ptoImpact.pto_details?.pto_days} day(s)</p>
                    </div>
                    <div className="p-4 border rounded-lg">
                      <p className="text-sm text-muted-foreground">Visits Affected</p>
                      <p className="text-xl font-bold text-amber-600">{ptoImpact.pto_details?.visits_affected}</p>
                    </div>
                    <div className="p-4 border rounded-lg">
                      <p className="text-sm text-muted-foreground">Coverage</p>
                      <p className={`text-xl font-bold ${
                        ptoImpact.coverage_analysis?.coverage_percentage >= 100 ? 'text-emerald-600' : 'text-amber-600'
                      }`}>
                        {ptoImpact.coverage_analysis?.coverage_percentage}%
                      </p>
                    </div>
                  </div>

                  <div>
                    <h4 className="font-semibold mb-3">Coverage Options</h4>
                    <div className="space-y-2">
                      {ptoImpact.coverage_analysis?.options?.map((option, idx) => (
                        <div key={idx} className="flex items-center justify-between p-3 border rounded-lg">
                          <span className="font-medium">{option.nurse_name}</span>
                          <div className="flex items-center gap-4">
                            <span className="text-sm">Current: {option.current_workload} visits</span>
                            <Badge className="bg-emerald-100 text-emerald-700">
                              Can cover {option.can_cover_visits}
                            </Badge>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className={`p-4 rounded-lg ${
                    ptoImpact.impact_level === 'high' ? 'bg-red-50' :
                    ptoImpact.impact_level === 'medium' ? 'bg-amber-50' : 'bg-emerald-50'
                  }`}>
                    <h4 className="font-semibold mb-2">Recommendations</h4>
                    {ptoImpact.recommendations?.map((rec, idx) => (
                      <p key={idx} className="text-sm">
                        <strong>{rec.action}:</strong> {rec.detail}
                      </p>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="text-center py-8">
                  <Calendar className="h-12 w-12 text-muted-foreground mx-auto mb-3" />
                  <p className="text-muted-foreground">Click "Analyze Impact" to see PTO coverage analysis</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
